import { ReactNode } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import {
  cardSx,
  cardTitleSx,
  detailsLabelSx,
  detailsPillsSx,
  detailsRowSx,
  detailsValueSx,
  neutralPillSx,
  priorityPillSx,
  statusPillSx,
} from "../ViewTicket.styles";

interface CustomProps {
  ticketDetails: any;
  isLoading: boolean;
}

/**
 * Details rail beside the description: one label / value line per field, ruled
 * off from each other, with the status, assignee and priority carried on pills.
 */
const TicketDetails = ({ ticketDetails, isLoading }: CustomProps) => {
  const assignees = (ticketDetails?.assigneeName ?? "")
    .split(",")
    .map((name: string) => name.trim())
    .filter(Boolean);

  const getRow = (label: string, value: ReactNode, isLast = false) => (
    <Box key={label} sx={detailsRowSx(isLast)}>
      <Typography component="span" sx={detailsLabelSx}>
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton animation="wave" variant="text" height={14} width={90} />
      ) : (
        value
      )}
    </Box>
  );

  return (
    <Box sx={cardSx}>
      <Typography component="h2" sx={cardTitleSx}>
        Details
      </Typography>

      {getRow(
        "Status",
        <Box component="span" sx={statusPillSx(ticketDetails?.status)}>
          {ticketDetails?.status}
        </Box>,
      )}

      {getRow(
        "Assignee",
        <Box sx={detailsPillsSx}>
          {assignees.length > 0 ? (
            assignees.map((name: string) => (
              <Box key={name} component="span" sx={neutralPillSx}>
                {name}
              </Box>
            ))
          ) : (
            <Box component="span" sx={neutralPillSx}>
              Unassigned
            </Box>
          )}
        </Box>,
      )}

      {getRow(
        "Reporter",
        <Typography component="span" sx={detailsValueSx}>
          {ticketDetails?.assignorName}
        </Typography>,
      )}

      {getRow(
        "Category",
        <Typography component="span" sx={detailsValueSx}>
          {ticketDetails?.process}
        </Typography>,
      )}

      {getRow(
        "Priority",
        <Box component="span" sx={priorityPillSx(ticketDetails?.priority)}>
          {ticketDetails?.priority}
        </Box>,
        true,
      )}
    </Box>
  );
};

export default TicketDetails;
